import { useEffect, useState } from 'react';
import type { ExtractedEvents } from '../../av/events';
import type { AVSessionController } from '../../av/sessionController';
import { TICKS_PER_BAR, TICKS_PER_BEAT, formatPosition, formatSeconds, parseTimeInput, secondsToFrame, secondsToTicks, ticksToSeconds } from '../../av/time';
import * as av from '../../domain/avOps';
import type { AvSession, Project } from '../../domain/schemas';
import { useObservable } from '../../lib/useObservable';
import type { SyncSelectionState } from '../../state/syncSelection';
import type { EditFn } from '../scene/useSceneEdit';
import { Button, inputClass } from '../ui';

/** Play / pause / stop / restart, loop range, position readout and playback status. */
export function SyncTransport({ project, session, sync, edit }: { project: Project; session: AvSession; sync: AVSessionController; edit: EditFn }) {
  const state = useObservable(sync.state);
  const running = state.status === 'playing' || state.status === 'starting';
  const bars = Math.max(1, Math.round(av.musicTicks(project, session) / TICKS_PER_BAR));
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2" role="toolbar" aria-label="Sync playback">
        <Button variant="primary" size="sm" onClick={() => void sync.toggle()} aria-pressed={running} data-testid="sync-toggle">
          {running ? 'Pause' : state.status === 'paused' ? 'Resume' : 'Play'}
        </Button>
        <Button size="sm" onClick={() => sync.stop()} data-testid="sync-stop">
          Stop
        </Button>
        <Button size="sm" onClick={() => void sync.restart()} data-testid="sync-restart">
          Restart
        </Button>
        <label className="flex items-center gap-1 text-xs text-zinc-300">
          <input type="checkbox" checked={session.loop.enabled} onChange={(e) => edit(e.target.checked ? 'Loop on' : 'Loop off', (p) => av.setLoop(p, session.id, { enabled: e.target.checked }))} data-testid="sync-loop" />
          Loop bars
        </label>
        <LoopBars key={`${session.id}:${session.loop.startTick}:${session.loop.endTick}`} session={session} bars={bars} edit={edit} />
        <label className="flex items-center gap-1 text-xs text-zinc-300">
          <input type="checkbox" checked={session.muted} onChange={(e) => edit(e.target.checked ? 'Mute session' : 'Unmute session', (p) => av.updateSession(p, session.id, { muted: e.target.checked }))} data-testid="sync-muted" />
          Mute (silent preview)
        </label>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <GoTo project={project} sync={sync} />
        <Position project={project} session={session} sync={sync} />
      </div>
      <p className="text-xs text-zinc-400" role="status" data-testid="sync-status" data-status={state.status} data-source={state.source ?? ''}>
        {state.status === 'starting' ? 'Starting audio…' : state.status}
        {state.source === 'audio' ? ' · clock: audio (sound and picture share the audio clock)' : state.source === 'system' ? ' · clock: system (silent)' : ''}
        {state.notice ? ` · ${state.notice}` : ''}
      </p>
    </div>
  );
}

function GoTo({ project, sync }: { project: Project; sync: AVSessionController }) {
  const [text, setText] = useState('');
  const [invalid, setInvalid] = useState(false);
  const go = () => {
    const seconds = parseTimeInput(text, project.settings.bpm);
    setInvalid(seconds === null);
    if (seconds !== null) void sync.seek(seconds);
  };
  return (
    <input
      className={`${inputClass} w-28!`}
      placeholder="Go to 3.2"
      aria-label="Go to position (bar.beat, bar.beat.tick or seconds like 12.5s)"
      aria-invalid={invalid}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onKeyDown={(e) => e.key === 'Enter' && go()}
      data-testid="sync-goto"
    />
  );
}

/** Loop range in whole bars (local text until committed; re-keyed when the saved loop changes). */
function LoopBars({ session, bars, edit }: { session: AvSession; bars: number; edit: EditFn }) {
  const [from, setFrom] = useState(String(Math.floor(session.loop.startTick / TICKS_PER_BAR) + 1));
  const [to, setTo] = useState(String(Math.ceil(session.loop.endTick / TICKS_PER_BAR)));
  const apply = () => edit('Set loop', (p) => av.loopBars(p, session.id, Number(from), Number(to)));
  return (
    <>
      <input aria-label="Loop from bar" className={`${inputClass} w-16!`} type="number" min={1} max={bars} value={from} onChange={(e) => setFrom(e.target.value)} onBlur={apply} onKeyDown={(e) => e.key === 'Enter' && apply()} data-testid="sync-loop-from" />
      <span className="text-xs text-zinc-400">to</span>
      <input aria-label="Loop to bar" className={`${inputClass} w-16!`} type="number" min={1} max={bars} value={to} onChange={(e) => setTo(e.target.value)} onBlur={apply} onKeyDown={(e) => e.key === 'Enter' && apply()} data-testid="sync-loop-to" />
    </>
  );
}

/** Live position readout (bar.beat.tick, seconds, frame); re-renders every animation frame while playing, nothing else does. */
function Position({ project, session, sync }: { project: Project; session: AvSession; sync: AVSessionController }) {
  const clock = useObservable(sync.clock);
  const [, tick] = useState(0);
  useEffect(() => {
    if (!clock.playing) return;
    let raf = 0;
    const loop = () => {
      tick((n) => n + 1);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [clock.playing]);
  const sample = sync.clock.sample();
  const ticks = Math.round(secondsToTicks(sample.seconds, project.settings.bpm));
  const anim = session.animationId ? project.animations.find((a) => a.id === session.animationId) : undefined;
  return (
    <p className="font-mono text-sm text-zinc-200" data-testid="sync-position" data-seconds={sample.seconds.toFixed(4)} data-frame={sample.frame}>
      {formatPosition(ticks)} · {formatSeconds(sample.seconds)} · frame {sample.frame}
      {anim ? ` @ ${anim.fps} fps` : ''} · {formatSeconds(clock.timing.durationSec)} total
    </p>
  );
}

/** Timing of the selected event (or the playhead) in every unit the session uses. */
export function TimingInspector({ project, session, events, sel, sync }: { project: Project; session: AvSession; events: ExtractedEvents; sel: SyncSelectionState; sync: AVSessionController }) {
  const clock = useObservable(sync.clock);
  const bpm = project.settings.bpm;
  const event = sel.eventId ? events.events.find((e) => e.id === sel.eventId) : undefined;
  const ticks = event ? event.tick : Math.round(secondsToTicks(clock.originSec, bpm));
  const seconds = ticksToSeconds(ticks, bpm);
  const anim = session.animationId ? project.animations.find((a) => a.id === session.animationId) : undefined;
  const fps = anim?.fps ?? 24;
  const local = seconds - ticksToSeconds(session.animationStartTick, bpm);
  const rows: [string, string][] = [
    ['Position', formatPosition(ticks)],
    ['Seconds', seconds.toFixed(4)],
    ['Beats', (ticks / TICKS_PER_BEAT).toFixed(3)],
    ['Ticks (PPQ 192)', String(ticks)],
    [`Frame (${fps} fps${anim ? '' : ', no animation'})`, local < 0 ? `before start (${secondsToFrame(seconds, fps)})` : String(secondsToFrame(local, fps))],
  ];
  if (event) {
    rows.unshift(['Event', `${av.CATEGORY_LABELS[event.category]} · ${event.source.kind === 'marker' ? 'manual marker' : 'extracted'}`]);
    rows.push(['Velocity', event.velocity.toFixed(2)], ['Id', event.id], ['Extraction', `v${event.version}`]);
  }
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs" data-testid="sync-inspector">
      <dt className="col-span-2 mb-1 text-zinc-400">{event ? 'Selected event' : 'Playhead (when paused)'}</dt>
      {rows.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="text-zinc-400">{k}</dt>
          <dd className="font-mono break-all text-zinc-200">{v}</dd>
        </div>
      ))}
    </dl>
  );
}
